import { useEffect, useState } from "react";

import { SelectItem } from "./types";

export interface UseSelectProps<TItem extends SelectItem> {
  items: TItem[];
  initialSelectedItem?: TItem;
  onChange?: (item: TItem) => void;
  createItem?: (item: TItem) => void;
}

export const useSelect = <TItem extends SelectItem>({
  items,
  initialSelectedItem,
  onChange,
  createItem,
}: UseSelectProps<TItem>) => {
  const [selectedItem, setSelectedItem] = useState<TItem | undefined>(initialSelectedItem);

  useEffect(() => {
    setSelectedItem(initialSelectedItem);
  }, [initialSelectedItem]);

  const doesItemExist = (item: TItem) => items.some((i) => i.id === item.id);

  const onItemSelect = (item: TItem) => {
    if (!doesItemExist(item) && createItem) createItem(item);
    setSelectedItem(item);
    if (onChange) onChange(item);
  };

  const isItemSelected = (item: TItem) => selectedItem?.id === item.id;

  return { items, selectedItem, onItemSelect, isItemSelected };
};

export type UseSelect<TItem extends SelectItem> = ReturnType<typeof useSelect<TItem>>;
